import { Injectable, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UnleashService } from "@karelics/angular-unleash-proxy-client";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, map } from "rxjs";

import { FeatureFlag } from "features/feature-flags/models/feature-flag";
import { CorityService } from "services/cority.service";
import { FormattersService } from "services/formatters.service";
import { AuthFacade, AuthState } from "state/auth.facade";
import { OrgFacade } from "state/org.facade";

import {
  CORITY_RESTRICTED_ROUTES,
  CREATE_ROUTE,
  getNavLinks,
} from "../models/nav-header.constants";
import { NavLink, NavLinkConfig } from "../models/nav-header.interfaces";

@Injectable({ providedIn: "root" })
export class NavLinksService {
  private readonly authFacade = inject(AuthFacade);
  private readonly orgFacade = inject(OrgFacade);
  private readonly translate = inject(TranslateService);
  private readonly formatterService = inject(FormattersService);
  private readonly corityService = inject(CorityService);
  private readonly unleashService = inject(UnleashService);

  private readonly nav = toSignal(
    combineLatest([
      this.authFacade.state$,
      this.orgFacade.explicitOrgId$,
      this.unleashService.isEnabled$(FeatureFlag.ManualAssessment),
    ]).pipe(
      map(([authState, explicitOrgId, isManualAssessmentEnabled]) =>
        this.build(authState, explicitOrgId, isManualAssessmentEnabled)
      )
    ),
    { initialValue: { links: [] as NavLink[], showCreate: false } }
  );

  readonly links = computed(() => this.nav().links);

  readonly showCreate = computed(() => this.nav().showCreate);

  private build(
    authState: AuthState,
    explicitOrgId: string | null,
    isManualAssessmentEnabled: boolean
  ): { links: NavLink[]; showCreate: boolean } {
    const visible = getNavLinks(this.translate).filter((link) =>
      this.isVisible(link, authState)
    );

    return {
      links: visible
        .filter((link) => link.route !== CREATE_ROUTE || !isManualAssessmentEnabled)
        .map((link) => ({
          route: link.route,
          label:
            typeof link.label === "function"
              ? link.label(authState.org, this.formatterService)
              : link.label,
          queryParams:
            link.supportsDevMode && explicitOrgId ? { orgId: explicitOrgId } : undefined,
        })),
      showCreate:
        isManualAssessmentEnabled && visible.some((link) => link.route === CREATE_ROUTE),
    };
  }

  private isVisible(link: NavLinkConfig, authState: AuthState): boolean {
    const shouldDisplay =
      typeof link.shouldBeDisplayed === "function"
        ? link.shouldBeDisplayed(authState)
        : (link.shouldBeDisplayed ?? true);

    if (CORITY_RESTRICTED_ROUTES.includes(link.route) && authState.org?.name) {
      return (
        !this.corityService.isCorityOrganization(authState.org.externalClientApiKeyId) &&
        shouldDisplay
      );
    }

    return shouldDisplay;
  }
}
